import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Data, NavigationEnd, Router } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { filter, map, takeUntil, switchMap, take } from "rxjs/operators";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import {
    FuseHorizontalNavigationComponent,
    FuseNavigationService,
} from "@fuse/components/navigation";
import { InitialData } from "app/app.types";
import { Scheme } from "app/fuse-core/config/app.config";
import { FuseConfigService } from "@fuse/services/config";
import { AuthService } from "app/core/services/auth.service";
import { Role } from "app/core/models/auth/role";
import { LocalStorageService } from "app/core/services/local-storage.service";
import { Store } from "@ngrx/store";
import { AppState } from "app/core/store/reducers/app.reducer";
import { SetProject } from "app/core/store/actions/common.actions";
import { FormProject } from "app/core/models/form";
import { LOCAL_STORAGE_KEYS } from "app/core/constants/local-storage-keys";
import { selectProject } from "app/core/store/selectors";

@Component({
    selector: "modern-layout",
    templateUrl: "./modern.component.html",
    encapsulation: ViewEncapsulation.None,
})
export class ModernLayoutComponent implements OnInit, OnDestroy {
    data: InitialData;
    isScreenSmall: boolean;
    role: string;
    leftNavigationOpen: boolean;
    subscription: Subscription;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseConfigService: FuseConfigService,
        private _authService: AuthService,
        private _localStorage: LocalStorageService,
        private _store: Store<AppState>,
        private localStorage: LocalStorageService
    ) {
        this.restoreLeftMenuOpenState();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this._authService.restoreAuthState();
        this.restoreSelectedProject();
        // Subscribe to the resolved route mock-api
        this._activatedRoute.data.subscribe((data: Data) => {
            this.data = data.initialData;
            this.getRole();
        });

        this.subscription = this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                filter((event: NavigationEnd) => event.url === "/home"),
                switchMap(() =>
                    this._store.select(selectProject).pipe(take(1))
                ),
                filter((project) => !!project && !!project.id),
                map((project) => ({
                    ...project,
                    code: project.code.toLowerCase(),
                }))
            )
            .subscribe((project) => {
                this._router.navigate([`/project/${project.code}/home`]);
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes("md");

                if (this.isScreenSmall) {
                    this.leftNavigationOpen = false;
                } else {
                    this.restoreLeftMenuOpenState();
                }
            });
    }

    restoreSelectedProject() {
        let selectedProject = this._localStorage.get<FormProject>(
            LOCAL_STORAGE_KEYS.SelectedProject
        );
        if (selectedProject) {
            this._store.dispatch(new SetProject(selectedProject));
        }
    }

    getRole() {
        this._authService.getRole().subscribe((role) => {
            this.role = role;
        });
    }

    restoreLeftMenuOpenState() {
        this.leftNavigationOpen = this.localStorage.get(
            LOCAL_STORAGE_KEYS.LeftMenuOpenState
        );
    }

    setScheme(event): void {
        let scheme: Scheme = "light";
        if (event.checked) {
            scheme = "dark";
        }
        this._fuseConfigService.config = { scheme };
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    openedChanged(isOpen) {
        if (this.isScreenSmall) {
            this.leftNavigationOpen = isOpen;
        }
    }

    toggleLeftMenu() {
        this.leftNavigationOpen = !this.leftNavigationOpen;

        if (!this.isScreenSmall) {
            this.localStorage.set(
                LOCAL_STORAGE_KEYS.LeftMenuOpenState,
                this.leftNavigationOpen
            );
        }
    }
}
